import { CheckCircleIcon, InfoCircleIcon } from '@vizit/icons';
import { cva, cx } from '@vizit/theme';
import * as React from 'react';
import { AlertLevel } from './types';

const iconClasses = cva({
  base: 'h-fit w-fit',
  variants: {
    level: {
      default: 'p-5 text-secondary border border-solid border-primary shadow-inset-skeumorphic-border',
      info: 'p-1 outline outline-offset-2 outline-brand-600/10 rounded-full border border-solid text-brand-600 border-brand-600/30',
      error:
        'p-1 outline outline-offset-2 outline-error-600/10 rounded-full border border-solid text-error-600 border-error-primary/30',
      success:
        'p-1 outline outline-offset-2 outline-success-600/10 rounded-full border border-solid text-success-600 border-success-primary/30',
      warning:
        'p-1 outline outline-offset-2 outline-warning-600/10 rounded-full border border-solid text-warning-600 border-warning-primary/30',
    },
  },
});

const rootClasses = cva({
  base: 'flex flex-col sm:flex-row gap-8 p-8 bg-primary',
  variants: {
    border: {
      true: 'border border-solid border-primary shadow-xs',
      false: '',
    },
  },
});

interface AlertProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * The alert type (represents severity).
   *
   * @optional
   * @default 'info'
   * @type 'error' | 'info' | 'success' | 'warning'
   */
  level?: AlertLevel;
  /**
   * Whether to show a border around the alert.
   *
   * @optional
   * @default true
   * @type boolean
   */
  border?: boolean;
}

function Alert(props: AlertProps) {
  const { border = true, children, className, level = AlertLevel.Default, ...other } = props;

  const icon = React.useMemo(
    () =>
      ({
        [AlertLevel.Default]: <InfoCircleIcon size={20} />,
        [AlertLevel.Info]: <InfoCircleIcon size={20} />,
        [AlertLevel.Error]: <InfoCircleIcon size={20} />,
        [AlertLevel.Success]: <CheckCircleIcon size={20} />,
        [AlertLevel.Warning]: <InfoCircleIcon size={20} />,
      })[level],
    [level],
  );

  return (
    <div {...other} className={cx(rootClasses({ border }), className)} role="alert">
      <div className={iconClasses({ level })}>{icon}</div>
      <div className="flex flex-col gap-2 justify-center">{children}</div>
    </div>
  );
}

export { Alert };
